import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createConversation,
  fetchAdminUsers,
  fetchConversationMessages,
  fetchConversations,
  sendMessage,
} from "../services/adminMessagingService";
import { getStoredAuthUser } from "../utils/backendApi";

const CONVERSATIONS_QK = ["admin", "conversations"];
const DIRECTORY_QK = ["admin", "directory"];

export const useAdminDirectory = (options = {}) =>
  useQuery({
    queryKey: DIRECTORY_QK,
    queryFn: fetchAdminUsers,
    staleTime: 60 * 1000,
    ...options,
  });

export const useAdminConversations = (options = {}) => {
  const authUser = getStoredAuthUser();

  return useQuery({
    queryKey: [...CONVERSATIONS_QK, authUser?.id],
    queryFn: () => fetchConversations(authUser?.id),
    enabled: Boolean(authUser?.id),
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,
    refetchOnWindowFocus: true,
    ...options,
  });
};

export const useAdminConversationMessages = (conversationId, options = {}) =>
  useQuery({
    queryKey: [...CONVERSATIONS_QK, "messages", conversationId],
    queryFn: () => fetchConversationMessages(conversationId),
    enabled: Boolean(conversationId),
    staleTime: 10 * 1000,
    ...options,
  });

export const useCreateAdminConversation = () => {
  const qc = useQueryClient();
  const authUser = getStoredAuthUser();

  return useMutation({
    mutationFn: (userId) => createConversation(userId, authUser?.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CONVERSATIONS_QK });
    },
  });
};

export const useSendAdminMessage = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: sendMessage,
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: CONVERSATIONS_QK });
      qc.invalidateQueries({
        queryKey: [...CONVERSATIONS_QK, "messages", variables?.conversationId],
      });
    },
  });
};
